<template>
  <div class="dynamic-page-wrapper">
    <div
      v-if="isPreviewMode"
      class="page-preview-banner"
      role="status"
    >
      Предпросмотр{{ page && !page.published ? ' черновика' : '' }} — страница
      {{ page && page.published ? 'открыта по ссылке предпросмотра' : 'ещё не опубликована на сайте' }}
    </div>
    <div class="dynamic-page-margin">
      <div v-if="isLoading" class="loading">
      </div>

      <!-- Ошибка -->
      <div v-else-if="error" class="error">
        Страница не найдена или произошла ошибка
        <router-link to="/">Вернуться на главную</router-link>
      </div>

      <div v-else-if="page" class="page-structure">
        <!-- Верх: меню + hero (flex, без float — как на /about/specialists) -->
        <div v-if="showDynamicMenu || firstBlockIsTop" class="dynamic-page-top">
          <div v-if="showDynamicMenu" class="dynamic-page-side-menu">
            <DynamicMenu :menu-items="dynamicMenuItems" :current-slug="page.slug"/>
          </div>
          <div class="dynamic-page-top-main">
            <div v-if="firstBlockIsTop" class="hero-wrapper" :class="{ 'hero-wrapper--department-gallery': firstBlockIsDepartmentGallery }">
              <component
                  :is="getBlockComponent(page.blocks[0].type)"
                  :data="page.blocks[0].data"
                  :class="getBlockClass(page.blocks[0])"
              />
            </div>
          </div>
        </div>

        <!-- Блоки на всю ширину контейнера; staff_list — отдельная секция в 2 колонки -->
        <div class="dynamic-page-blocks">
          <template v-for="(segment, index) in blockSegments">
            <div
                v-if="segment.kind === 'staff_group'"
                :key="'staff-group-' + index"
                class="dynamic-page-staff-section"
            >
              <AppStaffListGroupBlock
                  class="block-staff_list"
                  :blocks="segment.blocks.map((b) => b.data)"
              />
            </div>
            <div
                v-else
                :key="'block-' + (segment.block.id || index)"
                class="content-wrapper"
                :class="{ 'content-wrapper--carousel': segment.block.type === 'carousel' }"
            >
              <component
                  :is="getBlockComponent(segment.block.type)"
                  :data="segment.block.data"
                  :class="getBlockClass(segment.block)"
              />
            </div>
          </template>
        </div>
      </div>

      <!-- Страница не найдена -->
      <div v-else class="error">
        Страница не найдена
        <router-link to="/">Вернуться на главную</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { actionTypes as pagesActionTypes } from "@/store/modules/pages";
import AppHeroBlock from "@/components/Blocks/AppHeroBlock.vue";
import AppHeroNewBlock from "@/components/Blocks/AppHeroNewBlock.vue";
import AppImageTextBlock from "@/components/Blocks/AppImageTextBlock.vue";
import AppHtmlContentBlock from "@/components/Blocks/AppHtmlContentBlock.vue";
import DynamicMenu from "@/components/DynamicMenu.vue";
import {actionTypes as menuItemsActionTypes} from "@/store/modules/menuItems";
import AppEditorBlock from "@/components/Blocks/AppEditorBlock.vue";
import AppAccordionListBlock from "@/components/Blocks/AppAccordionListBlock.vue";
import AppCarouselBlock from "@/components/Blocks/AppCarouselBlock.vue";
import AppStaffListBlock from "@/components/Blocks/AppStaffListBlock.vue";
import AppStaffListGroupBlock from "@/components/Blocks/AppStaffListGroupBlock.vue";
import AppStaffGrid4Block from "@/components/Blocks/AppStaffGrid4Block.vue";
import AppDepartmentInfoBlock from "@/components/Blocks/AppDepartmentInfoBlock.vue";
import AppLayoutColumnsBlock from "@/components/Blocks/AppLayoutColumnsBlock.vue";
import AppLayoutGridBlock from "@/components/Blocks/AppLayoutGridBlock.vue";
import AppListingCardsBlock from "@/components/Blocks/AppListingCardsBlock.vue";
import AppListingServiceSectionsBlock from "@/components/Blocks/AppListingServiceSectionsBlock.vue";
import AppDepartmentListBlock from "@/components/Blocks/AppDepartmentListBlock.vue";
import AppDepartmentGalleryBlock from "@/components/Blocks/AppDepartmentGalleryBlock.vue";
import AppUnderDepartmentListBlock from "@/components/Blocks/AppUnderDepartmentListBlock.vue";
import AppDepartmentHeroBlock from "@/components/Blocks/AppDepartmentHeroBlock.vue";
import AppDepartmentAboutBlock from "@/components/Blocks/AppDepartmentAboutBlock.vue";
import AppDepartmentTripleCardsBlock from "@/components/Blocks/AppDepartmentTripleCardsBlock.vue";
import AppDepartmentFactsBlock from "@/components/Blocks/AppDepartmentFactsBlock.vue";
import AppDepartmentContactsBlock from "@/components/Blocks/AppDepartmentContactsBlock.vue";
import AppDepartmentFactsContactsBlock from "@/components/Blocks/AppDepartmentFactsContactsBlock.vue";
import AppDepartmentReviewsBlock from "@/components/Blocks/AppDepartmentReviewsBlock.vue";
import AppThankYouBlock from "@/components/Blocks/AppThankYouBlock.vue";
import AppPaidServicesListingBlock from "@/components/Blocks/AppPaidServicesListingBlock.vue";
import AppFrequentSearchesBlock from "@/components/Blocks/AppFrequentSearchesBlock.vue";

export default {
  name: 'DynamicPage',

  components: {
    AppHeroBlock,
    AppImageTextBlock,
    AppHtmlContentBlock,
    AppEditorBlock,
    AppAccordionListBlock,
    AppCarouselBlock,
    AppStaffListBlock,
    AppStaffListGroupBlock,
    AppDepartmentInfoBlock,
    AppStaffGrid4Block,
    AppLayoutColumnsBlock,
    AppLayoutGridBlock,
    AppListingCardsBlock,
    AppListingServiceSectionsBlock,
    AppDepartmentListBlock,
    AppDepartmentGalleryBlock,
    AppUnderDepartmentListBlock,
    AppDepartmentHeroBlock,
    AppDepartmentAboutBlock,
    AppDepartmentTripleCardsBlock,
    AppDepartmentFactsBlock,
    AppDepartmentContactsBlock,
    AppDepartmentFactsContactsBlock,
    AppDepartmentReviewsBlock,
    AppThankYouBlock,
    AppHeroNewBlock,
    AppPaidServicesListingBlock,
    AppFrequentSearchesBlock,
    DynamicMenu,
  },

  data() {
    return {
      isLoading: false,
      error: null,
      dynamicMenuItems: [],
      searchState: { query: '' },
    };
  },

  provide() {
    return {
      searchState: this.searchState,
    };
  },

  computed: {
    ...mapState('pages', ['currentPage']),
    ...mapState('menuItems', ['childMenuItems']),

    page() {
      return this.currentPage;
    },

    isPreviewMode() {
      const q = this.$route?.query?.preview;
      return !!(q && String(q).length);
    },

    previewToken() {
      if (!this.isPreviewMode) return null;
      return String(this.$route.query.preview);
    },

    showDynamicMenu() {
      return this.page && this.page.linkedToMenu && this.page.menuItemId && this.dynamicMenuItems.length > 0;
    },

    firstBlockIsHero() {
      return this.page?.blocks?.length && (this.page.blocks[0].type === 'hero' || this.page.blocks[0].type === 'hero_new');
    },

    firstBlockIsDepartmentGallery() {
      return this.page?.blocks?.length && this.page.blocks[0].type === 'department_gallery';
    },

    firstBlockIsTop() {
      return this.firstBlockIsHero || this.firstBlockIsDepartmentGallery;
    },

    contentBlocks() {
      if (!this.page?.blocks?.length) return [];
      return this.firstBlockIsTop ? this.page.blocks.slice(1) : this.page.blocks;
    },

    /** Подряд идущие staff_list — один список в 2 колонки (на persvrach их 12 по 1 врачу) */
    blockSegments() {
      const segments = [];
      const blocks = this.contentBlocks;
      let i = 0;
      while (i < blocks.length) {
        const block = blocks[i];
        if (block.type !== "staff_list") {
          segments.push({ kind: "block", block });
          i += 1;
          continue;
        }
        const group = [];
        while (i < blocks.length && blocks[i].type === "staff_list") {
          group.push(blocks[i]);
          i += 1;
        }
        segments.push({ kind: "staff_group", blocks: group });
      }
      return segments;
    },
  },

  mounted() {
    this.updateDocumentTitle();
  },

  watch: {
    '$route.fullPath': {
      handler() {
        this.getPage();
      },
      immediate: true
    },

    page: {
      handler(newPage) {
        if (newPage) {
          this.updateDocumentTitle();
        }
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('pages', {
      getPageBySlug: pagesActionTypes.getPageBySlug
    }),
    ...mapActions('menuItems', {
      getChildMenuItemsByParent: menuItemsActionTypes.getChildMenuItemsByParent
    }),

    updateDocumentTitle() {
      if (this.page && this.page.title) {
        const previewPrefix = this.isPreviewMode ? '[Предпросмотр] ' : '';
        document.title = previewPrefix + this.page.title + ' | Алтайский краевой клинический перинатальный центр ДАР';
      } else {
        document.title = 'Перинатальный Центр ДАР';
      }
    },

    pageSlugFromRoute() {
      const p = this.$route.params.slug;
      if (Array.isArray(p)) return p.join('/');
      if (typeof p === 'string' && p.length) return p;
      return (this.$route.path || '').replace(/^\/+|\/+$/g, '');
    },

    async getPage() {
      const slug = this.pageSlugFromRoute();
      if (!slug) return;

      this.isLoading = true;
      this.error = null;
      this.dynamicMenuItems = [];
      this.searchState.query = '';

      try {
        await this.getPageBySlug({
          slug,
          previewToken: this.previewToken || undefined,
        });

        if (this.page) {
          if (this.page.linkedToMenu && this.page.menuItemId) {
            await this.loadChildMenuItems(this.page.menuItemId);
          }
        } else {
          this.error = 'Страница не найдена';
        }
      } catch (error) {
        console.error('Ошибка загрузки страницы:', error);
        this.error = 'Страница не найдена';
      } finally {
        this.isLoading = false;
      }
    },

    async loadChildMenuItems(parentId) {
      await this.getChildMenuItemsByParent(parentId);
      if (this.childMenuItems && this.childMenuItems.length > 0) {
        this.prepareDynamicMenuItems(this.childMenuItems);
      }
    },

    prepareDynamicMenuItems(menuItems) {
      this.dynamicMenuItems = menuItems.map(item => {
        const text = item.label || item.title || 'Без названия';
        const link = item.to || item.link || `/${item.id}`;

        let slug;
        if (link.startsWith('/')) {
          slug = link.substring(1);
        } else {
          slug = link;
        }

        if (slug.includes('?')) {
          slug = slug.split('?')[0];
        }

        const isActive = slug === this.page?.slug;

        return {
          id: item.id,
          text: text,
          link: link,
          slug: slug,
          active: isActive,
          order: item.order || 0
        };
      }).sort((a, b) => (a.order || 0) - (b.order || 0));
    },


    getBlockComponent(blockType) {
      const components = {
        'hero': 'AppHeroBlock',
        'hero_new': 'AppHeroNewBlock',
        'image_text': 'AppImageTextBlock',
        'html_content': 'AppHtmlContentBlock',
        'editor': 'AppEditorBlock',
        'accordion_list': 'AppAccordionListBlock',
        'carousel': 'AppCarouselBlock',
        'staff_list': 'AppStaffListBlock',
        'staff_grid_4': 'AppStaffGrid4Block',
        'department_info': 'AppDepartmentInfoBlock',
        'layout_columns': 'AppLayoutColumnsBlock',
        'layout_grid': 'AppLayoutGridBlock',
        'listing_cards': 'AppListingCardsBlock',
        'listing_service_sections': 'AppListingServiceSectionsBlock',
        'department_list': 'AppDepartmentListBlock',
        'department_gallery': 'AppDepartmentGalleryBlock',
        'under_department_list': 'AppUnderDepartmentListBlock',
        'department_hero': 'AppDepartmentHeroBlock',
        'department_about': 'AppDepartmentAboutBlock',
        'department_triple_cards': 'AppDepartmentTripleCardsBlock',
        'department_facts': 'AppDepartmentFactsBlock',
        'department_contacts': 'AppDepartmentContactsBlock',
        'department_facts_contacts': 'AppDepartmentFactsContactsBlock',
        'department_reviews': 'AppDepartmentReviewsBlock',
        'thank_you': 'AppThankYouBlock',
        'paid_services_listing': 'AppPaidServicesListingBlock',
        'frequent_searches': 'AppFrequentSearchesBlock',
      };
      return components[blockType] || 'AppHtmlContentBlock';
    },

    getBlockClass(block) {
      return `block-${block.type}`;
    }
  }
};
</script>

<style scoped lang="scss">

.dynamic-page-wrapper {
  min-height: 70vh;
  padding-top: 40px;
  padding-bottom: 20px;
  position: relative;
  background: #fff;
}

.page-preview-banner {
  position: sticky;
  top: 0;
  z-index: 1000;
  margin: -40px 0 16px;
  padding: 10px 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #5c3b00;
  background: #ffe8a3;
  border-bottom: 1px solid #e0c36a;
}

.dynamic-page-margin {
  margin: 0 30px;

  @media (max-width: 768px) {
    margin: 0 15px; // Уменьшаем отступы на планшетах
  }

  @media (max-width: 480px) {
    margin: 0 10px; // Минимальные отступы на мобильных
  }

  @media (max-width: 360px) {
    margin: 0 0px; // Совсем маленькие отступы для очень узких экранов
  }
}

.page-structure {
  position: relative;
  max-width: 1520px;
  margin: 0 auto;
}

.dynamic-page-top {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 20px;
}

.dynamic-page-side-menu {
  flex: 0 0 300px;
  position: sticky;
  top: 100px;
  z-index: 90;
  margin-top: 20px;
  padding-bottom: 40px;
}

.dynamic-page-top-main {
  flex: 1;
  min-width: 0;
  padding: 0 20px;
}

.hero-wrapper {
  min-height: 100px;
  margin-bottom: 20px;
}

.dynamic-page-blocks {
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    padding: 0 5px;
  }
}

.dynamic-page-staff-section {
  width: 100%;
  max-width: 100%;
  margin-bottom: 24px;
  clear: both;
}

.content-wrapper {
  max-width: 1600px;
  margin: 0 auto;
}

.content-wrapper--carousel {
  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
    max-width: none;
  }
}

@media (max-width: 1200px) {
  .dynamic-page-side-menu {
    flex: 0 0 280px;
  }

  .page-structure,
  .content-wrapper {
    max-width: 100%;
    padding: 0 15px;
  }
}

@media (max-width: 1087px) {
  .dynamic-page-top {
    flex-direction: column;
  }

  .dynamic-page-side-menu {
    flex: none;
    position: static;
    width: 100%;
    margin: 0 0 30px 0;
    padding: 0;
  }

  .dynamic-page-top-main {
    padding: 0;
  }

  .hero-wrapper {
    min-height: auto;
  }
}

@media (max-width: 768px) {
  .dynamic-page-wrapper {
    padding-top: 20px;
  }

  .dynamic-page-blocks,
  .page-structure {
    padding: 0 0;
  }
}
</style>

<!-- Без scoped: стили дочернего AppDoctorsPeopleList (Vue 2 не применяет :deep()) -->
<style lang="scss">
.dynamic-page-staff-section .people__list.grid-container {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 30px;
  width: 100%;
  align-items: stretch;

  &.grid-container--one {
    grid-template-columns: 1fr;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  > .grid-item {
    min-width: 0;
    width: 100%;
    max-width: 100%;
    display: flex;
    height: 100%;
  }
}
</style>